import { Fragment, useContext, useEffect, useRef, useState } from "react";
import Item from "../../../../components/Lists/Item";
import PartConfigurationGrid from "./PartConfigurationGrid";
import { DialogTrigger } from "../../../../components/Dialog/Dialog";
import DeletePartDialog from "./DeletePartDialog";
import { DropdownsContext } from "../../../../contexts/DropdownsContext";
import { usePermissions } from "../../../../hooks/usePermissions";
import { LABELS } from "../../../../globals/globals";
import { closeDraft, discardPart, editRow, fetchParts, nextPage, removePartAction, selectIdxParts, selectLoadingParts, selectOrder, selectPage, selectPartDraftSections, selectParts, selectPartsEditing, selectPartsFilters, selectSearch, setOrder } from "../../../../redux/features/configuration/parts/partsConfigurationSlice";
import { useDispatch, useSelector } from "react-redux";
import { useSnackBar } from "../../../../hooks/useSnackBar";
import { Expandable2 } from "../../../../components/Expandable/Expandable2";
import { Sorting } from "../../../../components/Lists/Sorting";
import { SortingHeader } from "../../../../components/Sorting/SortingHeader";
import { useGlobalSearch } from "../../../../hooks/useGlobalSearch";

const mapper = {
  partNumber: "PN",
  description: "Description",
  categoryName: "Category",
  subCategoryName: "Sub Category",
  ata: "ATA"
}
// const ataFormatter = (part) => ((part.ATAChapterNumber) ? (`${part.ATAChapterNumber}` + (part.ATASectionNumber ? (`-${part.ATASectionNumber}` + (part.ATAParagraphNumber ? `-${part.ATAParagraphNumber}` : '')) : '')) : '')
const ataFormatter = (part) => `${part.ATAChapterNumber ?? '00'}-${part.ATASectionNumber ?? '00'}-${part.ATAParagraphNumber ?? '00'}`

const mappedItem = (part) => ({
  PN: part.partNumber,
  Description: part.description,
  Category: part.categoryName,
  "Sub Category": part.subCategoryName,
  "ATA": ataFormatter(part)
});


const compoundProperties = {
  ata: (item) => {
    return ataFormatter(item)
  }
}

const dropdownIds = [4, 2, 5, 6, 7, 8, 9, 10, 1, 3, 29, 50, 51, 52, 53, 54, 56, 60, 64, 67, 70, 71, 72, 73, 75, 77, 78, 80, 81, 86, 17, 19, 15, 74, 94, 95, 98, 114, 115];
// const dropdownIds = [56, 60, 64, 67, 70, 71, 72, 73, 75, 77, 78, 80, 81, 86, 17, 19, 15, 74, 94, 95, 98, 114, 115];


const PartList = () => {
  const dispatch = useDispatch();

  const parts = useSelector(selectParts);
  const page = useSelector(selectPage);
  const filters = useSelector(selectPartsFilters);
  const orderBy = useSelector(selectOrder);
  const search = useSelector(selectSearch);
  const idxParts = useSelector(selectIdxParts);
  const loadingParts = useSelector(selectLoadingParts);
  const editingParts = useSelector(selectPartsEditing);
  const draftParts = useSelector(selectPartDraftSections);
  
  const isEditingPart = (part) => editingParts[part.id]?.includes('row');
  const isUnsavedPart = (part) => draftParts[part.id]?.includes('row');


  useEffect(() => {
    dispatch(fetchParts({ page }))
  }, [page, filters, orderBy]);

  useEffect(()=>{
    console.warn('*new list search', search);
    dispatch(fetchParts({page: 0, searchValue:search}));
  }, [search])

  useGlobalSearch((value)=>{
    dispatch(fetchParts({page: 0, searchValue:value}));
  })


  const { setDropdownIds } = useContext(DropdownsContext);
  const listRef = useRef(null);
  const { infoMsg, successMsg, warningMsg } = useSnackBar()


  const deletePart = (part) => {
    infoMsg("Removing part", `${part.partNumber ||  ""}`)
    dispatch(removePartAction(part.id)).then(reponse => successMsg("Part Deleted", `Part ${part.partNumber || ""} was removed`));
  }

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + 3) <= clientHeight) {
      if (!loadingParts) dispatch(nextPage());
    }
  }

  useEffect(() => {
    setDropdownIds(dropdownIds);
  }, []);

  const { hasPermission } = usePermissions();


  const deletePartUnsaved = (part) => {
    // errorMsg("Error"); 
    // dispatch(closeDraft({ partId: part.id, section: "general", additionalSections: ["row"] }));
    dispatch(discardPart(part));
    infoMsg("Part Discarded", "Unsaved Part was discarded");
  }

  const onOrderChange = (orderParam) => {
    dispatch(setOrder(orderParam));
  }

  const changeRow = (newValues, partId) => {
    if (newValues) {
      dispatch(editRow({
        partialValue: {
          ...newValues,
          ATAChapterNumber: newValues.aviationPart?.atachapterName ?? "00",
          ATASectionNumber: newValues.aviationPart?.atasectionName ?? "00",
          ATAParagraphNumber: newValues.aviationPart?.ataparagraphName ?? "00"
        }, partId
      }))
    }
  }

  return (
    <div className="part-list-table">
      <SortingHeader onFiltersSelected={()=>{}} />
      {/* {<Sorting showHeaderOrder onOrderChange={onOrderChange} properties={{ partNumber: "PN", description: "Description", categoryName: "Category", subCategoryName: "Sub Category", ATAChapterNumber: "ATA" }} />} */}
      <div className="part-list" ref={listRef} onScroll={handleScroll}>
        {
          parts.map((part, idx) =>
            <Fragment key={`${part.id}-${idx}`}>
              {/* {JSON.stringify(editingParts)}
              {JSON.stringify(draftParts)} */}
              <Expandable2 stickySummary key={`${part.id}-${idx}`} name="part-configuration-part" summary={
                <Item
                  orderedProperties={["partNumber", "description", "categoryName", "subCategoryName"]}
                  propertiesPlaceholder={{ ata: "00-00-00" }}
                  labelMapper={mapper}
                  valuePlaceholder="--"
                  computeProperties={compoundProperties}
                  ignoreProperties={["id", "ATAChapter", "ATASection", "ATAParagraph"]}
                  item={idxParts[part.id] ?? {}}
                  ribbon={[{
                    label: isUnsavedPart(part) ? LABELS.DRAFT : LABELS.EDITION_MODE,
                    when: isEditingPart(part) || isUnsavedPart(part),
                  }
                  ]}
                  hideLabels
                  itemSuffix={() =>
                    idxParts[part.id].version === 0 ?
                      <button onClick={() => deletePartUnsaved(part)} className="delete-part-button">
                        <i className="icon-delete-new"></i>
                      </button>
                      :
                      hasPermission('delete') &&
                      <DialogTrigger dialogContent={{
                        component: DeletePartDialog, props: {
                          item: mappedItem(idxParts[part.id]),
                          onDelete: () => deletePart(idxParts[part.id]),
                          message: "Are you sure you want to delete this part?"
                        }
                      }}
                      >
                        <div className="delete-part-button">
                          <i className="icon-delete-new"></i>
                        </div>
                      </DialogTrigger>
                  } />
              }>

                <PartConfigurationGrid
                  part={part}
                  open={open}
                  updatePart={(values) => changeRow(values, part.id)}
                />

              </Expandable2>
            </Fragment>
          )
        }
      </div>

    </div>

  )
}


export default PartList;